import { gql } from '@apollo/client';

/* ── the stock alpha platform ───────────────────────────────── */

/**
 * The stock alpha platform's reads (backend `graphql_endpoints/alpha_rank`),
 * screen by screen as backend docs/alpha_platform/30_pms_web_display.md lays
 * them out. Not the MF engine in ./alpha.js: that one ranks funds inside a
 * SEBI category; this one ranks stocks across a universe after a risk model.
 *
 * UNITS ARE NOT THE MF ENGINE'S. `alphaResid`, `sigmaSpecific`, weights,
 * returns and rates here are FRACTIONS (0.0016 is 16 bp), unlike the MF
 * schema's ready-made percentages. lib/stockAlpha.js does the scaling.
 *
 * Every row carries a symbol only — no name or sector (backend issue B1) — so
 * lists join names through STOCK_LABELS_BY_SYMBOLS in ./stocks.js, once per
 * page. Many reads are research-analyst only; their hooks report a refusal as
 * `denied` rather than an error.
 */

/* ── Ranks (§2.1) ─────────────────────────────────────────── */

/**
 * One horizon's ranking, best first. `percentile` is 100 = best (the MF
 * engine's counts the other way). `termStructure` is cumulative expected
 * residual return keyed by holding period in sessions.
 */
export const STOCK_ALPHA_RANKINGS = gql`
  query StockAlphaRankings($horizon: String!, $asOfDate: Date, $minPercentile: Float, $limit: Int!) {
    alphaRankings(
      horizon: $horizon
      asOfDate: $asOfDate
      minPercentile: $minPercentile
      limit: $limit
      includeExplanations: true
      includePersonas: true
    ) {
      horizon
      asOfDate
      totalScored
      modelVersionId
      rows {
        securitySymbol
        rank
        scoreZ
        percentile
        alphaResid
        sigmaSpecific
        coverage
        universeSize
        termStructure
        conflictFlag
        explanations { factorId contribution valueRaw percentile direction text }
        personas { personaId displayName clientDisplayName status }
      }
    }
  }
`;

/** Every fitted version for a horizon with its out-of-sample evidence and gates. */
export const STOCK_ALPHA_MODEL_VERSIONS = gql`
  query StockAlphaModelVersions($horizon: String, $limit: Int!) {
    alphaModelVersions(horizon: $horizon, limit: $limit) {
      id
      horizon
      versionTag
      modelType
      status
      isActive
      labelVersion
      trainFrom
      trainTo
      nTrainDates
      nFolds
      icOosMean
      icOosT
      icOosHitRate
      icUsed
      gatesPassed
      gateDetail
      promotable
      liveSince
      trialCount
      holdoutAccesses
      createdAt
    }
  }
`;

/* ── Why this stock (§2.2) ────────────────────────────────── */

/** A, B and F: forecasts at every horizon, every factor value, every persona result. */
export const STOCK_ALPHA_SECURITY = gql`
  query StockAlphaSecurity($securitySymbol: String!, $asOfDate: Date) {
    alphaSecurity(securitySymbol: $securitySymbol, asOfDate: $asOfDate) {
      securitySymbol
      asOfDate
      forecasts {
        horizon
        rank
        percentile
        scoreZ
        alphaResid
        sigmaSpecific
        universeSize
        conflictFlag
        explanations { factorId contribution valueRaw percentile direction text }
      }
      factors { factorId family name valueRaw valueProcessed percentile expectedSign }
      personas { personaId displayName clientDisplayName status score rank rules }
    }
  }
`;

/** C: the rules-only valuation — scenarios, Monte Carlo, guardrails — and its peers. */
export const STOCK_ALPHA_VALUATION = gql`
  query StockAlphaValuation($securitySymbol: String!) {
    alphaValuation(securitySymbol: $securitySymbol) {
      asOfDate
      modelType
      priceUsed
      scenarios { scenario valuePerShare upside }
      wacc
      impliedGrowth
      baseGrowth
      impliedRoic
      mcP10
      mcP50
      mcP90
      probValueAbovePrice
      guardrailPassed
      guardrailDetail
    }
    alphaPeers(securitySymbol: $securitySymbol) {
      asOfDate
      pool
      peers { peerSymbol similarity rank source }
    }
  }
`;

/** D: the recommendation for every horizon on the latest date. */
export const STOCK_ALPHA_RECOMMENDATION = gql`
  query StockAlphaRecommendation($securitySymbol: String!) {
    alphaRecommendation(securitySymbol: $securitySymbol) {
      horizon
      asOfDate
      action
      confidence
      confidencePoints
      confidenceReasons
      conflictFlag
      valuationUpsideBase
      valuationUpsideProbWeighted
      priceDisciplineBandHigh
      liquidityOk
      negativeEvent
      screensPassed
      screensFailed
      styleLenses
      held
      userOverlayAction
      disclaimer
    }
  }
`;

/** E: the analyst panel, promises, recent news and the company brief. */
export const STOCK_ALPHA_RESEARCH = gql`
  query StockAlphaResearch($securitySymbol: String!) {
    alphaAnalystPanel(securitySymbol: $securitySymbol) {
      lensId
      title
      asOf
      stanceLabel
      conviction
      convictionLabel
      summary
      evidenceFor
      evidenceAgainst
      wouldChangeMind
    }
    alphaPromises(securitySymbol: $securitySymbol) {
      id
      description
      targetDueDate
      status
    }
    alphaNews(securitySymbol: $securitySymbol, limit: 20) {
      id
      headline
      publisher
      publishedAt
      url
      events { eventType sentiment novelty }
    }
    alphaCompanyBrief(securitySymbol: $securitySymbol) {
      version
      text
      createdAt
    }
  }
`;

/** The persona definitions, for the shared disclaimer. */
export const STOCK_ALPHA_PERSONAS = gql`
  query StockAlphaPersonas {
    alphaPersonas { personaId internalDisplayName clientDisplayName disclaimer }
  }
`;

/* ── Books (§2.3) ─────────────────────────────────────────── */

/** Every model book's latest rebalance, headers and diagnostics. */
export const STOCK_ALPHA_BOOKS = gql`
  query StockAlphaBooks {
    alphaModelPortfolios {
      portfolioId
      kind
      asOfDate
      previousAsOf
      status
      nNames
      mandate
      diagnostics
    }
  }
`;

/** One book in full: holdings and the trades since the previous rebalance. */
export const STOCK_ALPHA_BOOK = gql`
  query StockAlphaBook($viewId: String, $mandateId: String) {
    alphaModelPortfolio(viewId: $viewId, mandateId: $mandateId) {
      portfolioId
      kind
      asOfDate
      previousAsOf
      status
      nNames
      mandate
      diagnostics
      disclaimer
      holdings {
        securitySymbol
        weight
        activeWeight
        alphaContrib
        riskContrib
        liquidityDays
        rankAtSelection
        retainedByBuffer
      }
      trades { securitySymbol side weightChange quantity estCostBps urgency reason }
    }
  }
`;

/* ── Theses (§2.4) — research analyst ─────────────────────── */

export const STOCK_ALPHA_THESES = gql`
  query StockAlphaTheses($status: String, $limit: Int!) {
    alphaTheses(status: $status, limit: $limit) {
      id
      securitySymbol
      horizon
      asOfDate
      version
      viewId
      status
      oneLineThesis
      criticVerdict
    }
  }
`;

/**
 * One thesis in full. `evidence` is what the writer was given — every cited
 * item with its own sentence — and is how a citation tag becomes a footnote.
 */
export const STOCK_ALPHA_THESIS = gql`
  query StockAlphaThesis($securitySymbol: String!, $horizon: String!, $version: Int) {
    alphaThesis(securitySymbol: $securitySymbol, horizon: $horizon, version: $version) {
      id
      securitySymbol
      horizon
      asOfDate
      version
      viewId
      status
      oneLineThesis
      sections
      citations
      changesSinceLastVersion
      criticVerdict
      checkReport
      evidence
      disclaimers
      reviewedAt
      complianceAt
      publishedAt
      reviewEvents { fromStatus toStatus employeeId employeeRole note createdAt }
    }
  }
`;

/* ── Paper trading (§2.5) — research analyst ──────────────── */

export const STOCK_ALPHA_PAPER_BOOKS = gql`
  query StockAlphaPaperBooks($kind: String) {
    alphaPaperPortfolios(kind: $kind) {
      id
      portfolioId
      kind
      horizon
      sourcePortfolioId
      twinOf
      status
      startedOn
      lastSession
      haltedOn
      haltReason
      haltedBy
      initialCapitalInr
    }
  }
`;

/** One paper book's NAV, fills and latest positions, plus its twin's NAV. */
export const STOCK_ALPHA_PAPER_BOOK = gql`
  query StockAlphaPaperBook($portfolioId: String!, $twinId: String!) {
    nav: alphaPaperNav(portfolioId: $portfolioId) {
      tradeDate nav cash dailyReturn turnover costBps nPositions
    }
    twinNav: alphaPaperNav(portfolioId: $twinId) {
      tradeDate nav
    }
    alphaPaperFills(portfolioId: $portfolioId) {
      tradeDate securitySymbol side quantity price notional explicitCost impactCost costBps signalDate
    }
    alphaPaperPositions(portfolioId: $portfolioId) {
      tradeDate securitySymbol quantity price weight
    }
  }
`;

export const STOCK_ALPHA_REPORTS = gql`
  query StockAlphaReports($kind: String, $limit: Int!) {
    alphaReports(kind: $kind, limit: $limit) { id kind asOf title status createdAt }
  }
`;

/* ── Health (§2.6) — research analyst ─────────────────────── */

export const STOCK_ALPHA_JOB_HEALTH = gql`
  query StockAlphaJobHealth($sessions: Int!) {
    alphaJobHealth(sessions: $sessions) {
      sessions
      healthy
      since
      jobs { jobName expected completed failed missing manual openAlerts healthy }
    }
  }
`;

export const STOCK_ALPHA_ALERTS = gql`
  query StockAlphaAlerts($status: String, $limit: Int!) {
    alphaAlerts(status: $status, limit: $limit) {
      id
      key
      severity
      source
      message
      status
      firstSeen
      lastSeen
      occurrences
      acknowledgedBy
      acknowledgedAt
      resolvedBy
      resolvedAt
    }
  }
`;

export const STOCK_ALPHA_MONITORS = gql`
  query StockAlphaMonitors {
    alphaMonitorLatest { id metric scope asOf value bandLow bandHigh status }
  }
`;

/* ── Factors (§2.8) ───────────────────────────────────────── */

/** The whole catalogue — also the factor-id → name/family lookup every screen uses. */
export const STOCK_FACTOR_CATALOG = gql`
  query StockFactorCatalog {
    factorCatalog {
      id
      family
      name
      definition
      formula
      inputs
      frequency
      expectedSign
      horizons
      applicability
      readinessTier
      feasibility
      feasibilityReason
      investorStyleTags
    }
  }
`;

export const STOCK_FACTOR_FEASIBILITY = gql`
  query StockFactorFeasibility {
    factorFeasibility { catalogVersion total implemented unimplemented unavailable perFamily perReadinessTier }
  }
`;

/* ── Alpha Research (§2.9) ────────────────────────────────── */

export const STOCK_ALPHA_EXPERIMENTS = gql`
  query StockAlphaExperiments($horizon: String, $limit: Int!) {
    alphaExperiments(horizon: $horizon, limit: $limit) {
      id horizon hypothesis author trialGroup configVersion dataWindowFrom dataWindowTo metrics createdAt
    }
  }
`;

/** One version's backtests and its model card (markdown). */
export const STOCK_ALPHA_MODEL_DETAIL = gql`
  query StockAlphaModelDetail($modelVersionId: Int!) {
    alphaBacktests(modelVersionId: $modelVersionId) {
      id mandate path costMultiplier aumInr start end metrics createdAt
    }
    alphaModelCard(modelVersionId: $modelVersionId, format: "markdown") {
      modelVersionId generatedAt markdown
    }
  }
`;

/* ── Exposure optimiser (§2.10) ───────────────────────────── */

/** A dry run of the concentrated builder over stored forecasts. Writes nothing. */
export const STOCK_ALPHA_OPTIMISE = gql`
  query StockAlphaOptimise(
    $horizon: String!
    $symbols: [String!]
    $topN: Int
    $minPercentile: Float
    $nNames: Int!
    $sectorCap: Float!
    $singleStockCap: Float!
    $minPosition: Float!
    $applyExclusions: Boolean!
  ) {
    alphaOptimiseDryRun(
      horizon: $horizon
      symbols: $symbols
      topN: $topN
      minPercentile: $minPercentile
      nNames: $nNames
      sectorCap: $sectorCap
      singleStockCap: $singleStockCap
      minPosition: $minPosition
      applyExclusions: $applyExclusions
    ) {
      status
      message
      horizon
      asOfDate
      universeSize
      eligible
      expectedAlpha
      weighting
      binding
      skippedForSectorCap
      droppedBelowMin
      excluded { securitySymbol reason }
      sectors { sector weight names atCap }
      holdings {
        securitySymbol sector rank percentile alphaResid sigmaSpecific weight alphaContrib atStockCap
      }
    }
  }
`;

/* ── Sample portfolios (My portfolios) ────────────────────── */

/**
 * Daily prices for a list of stocks in ONE request: one aliased
 * `getStockPriceHistory` field per symbol (`p0`, `p1`, …), each taking its
 * symbol as a variable — nothing from the list is pasted into the query text.
 * One document per list length, cached.
 */
const PRICE_DOCS = new Map();
export function stockPricesDocument(count) {
  if (!PRICE_DOCS.has(count)) {
    const vars = Array.from({ length: count }, (_, i) => `$s${i}: String!`).join(', ');
    const fields = Array.from({ length: count }, (_, i) => (
      `p${i}: getStockPriceHistory(securitySymbol: $s${i}, filter: $filter) { securitySymbol stockHistory { date close adjustedClose } }`
    )).join('\n    ');
    PRICE_DOCS.set(count, gql`
  query StockPrices${count}(${vars}, $filter: String!) {
    ${fields}
  }
`);
  }
  return PRICE_DOCS.get(count);
}

/**
 * Several optimiser dry runs in ONE request — one aliased
 * `alphaOptimiseDryRun` per recipe (`o0`, `o1`, …), each with its own
 * variables. Writes nothing.
 */
const OPTIMISE_DOCS = new Map();
export function optimiseManyDocument(count) {
  if (!OPTIMISE_DOCS.has(count)) {
    const vars = Array.from({ length: count }, (_, i) => (
      `$h${i}: String!, $t${i}: Int, $n${i}: Int!, $sc${i}: Float!, $cap${i}: Float!, $min${i}: Float!`
    )).join(', ');
    const fields = Array.from({ length: count }, (_, i) => `
    o${i}: alphaOptimiseDryRun(
      horizon: $h${i}, topN: $t${i}, nNames: $n${i}, sectorCap: $sc${i},
      singleStockCap: $cap${i}, minPosition: $min${i}, applyExclusions: true
    ) {
      status message horizon asOfDate universeSize eligible expectedAlpha weighting binding
      excluded { securitySymbol reason }
      sectors { sector weight names atCap }
      holdings { securitySymbol sector rank percentile alphaResid sigmaSpecific weight alphaContrib atStockCap }
    }`).join('');
    OPTIMISE_DOCS.set(count, gql`
  query StockAlphaOptimiseMany${count}(${vars}) {${fields}
  }
`);
  }
  return OPTIMISE_DOCS.get(count);
}

/* ── Company research reports ─────────────────────────────── */

/**
 * The initiating-coverage reports for one company, newest first — the same
 * report the platform renders to PDF, as markdown (charts are PDF-only; the
 * web view redraws the ones whose data is in the report's own tables).
 */
export const STOCK_COMPANY_REPORTS = gql`
  query StockCompanyReports($securitySymbol: String!, $limit: Int!) {
    alphaCompanyReports(securitySymbol: $securitySymbol, limit: $limit) {
      id
      securitySymbol
      asOf
      markdown
      knowledgeVersion
      createdBy
      createdAt
    }
  }
`;

/**
 * The raw data under a stock's factors: its latest filed statements, a year
 * of daily prices, the analyst estimates and the results announcements —
 * the datasets the factor catalog names as inputs (Why this stock, panel B).
 */
export const STOCK_RAW_INPUTS = gql`
  query StockRawInputs($securitySymbol: String!) {
    getStockFinancials(securitySymbol: $securitySymbol, limit: 12) {
      filingDate
      periodEndDate
      fiscalPeriod
      fiscalYear
      epsDiluted
      incomeStatement { totalRevenue grossProfit netIncome ebitda ebit }
      balanceSheet { totalAssets totalEquity totalDebt }
      cashFlow { operatingCashFlow capitalExpenditures freeCashFlow dividendsPaid }
    }
    getStockPriceHistory(securitySymbol: $securitySymbol, filter: "1y") {
      dataPoints
      stockHistory { date close volume }
    }
    getStockEstimates(securitySymbol: $securitySymbol) {
      estimatePeriod
      asOfDate
      numberOfAnalysts
      epsEstimateAvg
      revenueEstimateAvg
    }
    getStockEarnings(securitySymbol: $securitySymbol, limit: 4) {
      reportDate
      fiscalPeriod
      reportedEps
      estimatedEps
      surprisePercent
    }
  }
`;
